import { Car, Clock } from 'lucide-react';
import { RoutingStatus } from '../../shared';
import { RouteStatusBadge } from './RouteStatusBadge';
import { RouteTotals } from '../../shared';

interface DayPlanningRouteStatusProps {
  /** Current routing status */
  routingStatus: RoutingStatus & {
    status: import('@certhon/shared').RouteStatus;
    canAutoSort: boolean;
    canManualEdit: boolean;
    routingTimestamp?: Date | null;
  };
  /** Route totals (distance and duration) */
  routeTotals: RouteTotals | null;
  /** Whether routes are currently loading */
  isLoadingRoutes: boolean;
  /** Whether optimization mutation is pending */
  isOptimizing: boolean;
  /** Whether auto-optimization will trigger */
  willAutoOptimize: boolean;
  /** Callback to trigger route optimization */
  onOptimize: () => void;
}

/**
 * Day Planning Route Status Component
 *
 * Shows the current route status badge and calculated route totals
 * (distance and duration). Only shown in single-technician view.
 */
export function DayPlanningRouteStatus({
  routingStatus,
  routeTotals,
  isLoadingRoutes,
  isOptimizing,
  willAutoOptimize,
  onOptimize,
}: DayPlanningRouteStatusProps) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <RouteStatusBadge
        status={routingStatus.status}
        canAutoSort={routingStatus.canAutoSort}
        canManualEdit={routingStatus.canManualEdit}
        onOptimize={onOptimize}
        isOptimizing={isOptimizing || willAutoOptimize}
        routingTimestamp={routingStatus.routingTimestamp}
      />

      {/* Route totals */}
      {routeTotals && !isLoadingRoutes && (
        <div className="text-muted-foreground flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1">
            <Car className="h-4 w-4" />
            {routeTotals.distanceStr}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {routeTotals.durationStr}
          </span>
        </div>
      )}
    </div>
  );
}
